import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  UserGroupIcon,
  CalendarIcon,
  BookOpenIcon,
  EllipsisVerticalIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import '../../styles/components.css';
import '../../styles/dark-mode.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConcessionTypes } from '../../utils/concessions';
import ConfirmModal from '../common/ConfirmModal';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { types: concessionTypes } = useConcessionTypes(true);
  const [stats, setStats] = useState(null);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [classHistory, setClassHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [classesPerPage] = useState(6);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showClassBookingsModal, setShowClassBookingsModal] = useState(false);
  const [classBookings, setClassBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistUsers, setWaitlistUsers] = useState([]);
  const [loadingWaitlist] = useState(false);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [loadingCancellations, setLoadingCancellations] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings', 'waitlist', or 'cancellations'
  const [classesActiveTab, setClassesActiveTab] = useState('upcoming'); // 'upcoming' or 'history'
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [classToCancel, setClassToCancel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Pagination logic - limit to first 10 pages for relevance
  const currentClassesList = classesActiveTab === 'upcoming' ? upcomingClasses : classHistory;
  const totalPages = Math.min(10, Math.ceil(currentClassesList.length / classesPerPage));
  const startIndex = (currentPage - 1) * classesPerPage;
  const endIndex = startIndex + classesPerPage;
  const currentClasses = currentClassesList.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [dashboardResponse, upcomingClassesResponse] = await Promise.all([
        axios.get('/api/admin/dashboard'),
        axios.get('/api/admin/upcoming-classes')
      ]);
      setStats(dashboardResponse.data);
      
      // Use the dedicated upcoming classes endpoint which returns the next 10 classes
      setUpcomingClasses(upcomingClassesResponse.data.upcomingClasses);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get('/api/admin/class-history');
      setClassHistory(response.data.classHistory);
    } catch (error) {
      console.error('Error fetching class history:', error);
      toast.error('Failed to fetch class history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleClassesTabChange = (tab) => {
    setClassesActiveTab(tab);
    if (tab === 'history' && classHistory.length === 0) {
      fetchClassHistory();
    }
  };

  const handleEditClass = (classItem) => {
    setSelectedClass(classItem);
    setEditForm({
      name: classItem.name || '',
      description: classItem.description || '',
      instructor: classItem.instructor || '',
      location: classItem.location || '',
      startTime: classItem.startTime ? (() => {
        const date = new Date(classItem.startTime);
        if (isNaN(date.getTime())) return '';
        
        // Use local time values to display exactly what the user selected
        // This ensures the date/time is displayed as New Zealand local time
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      })() : '',
      duration: classItem.duration || 60,
      maxCapacity: classItem.maxCapacity || 20,
      concessionsRequired: classItem.concessionsRequired || 1,
      concessionType: classItem.concessionType || '',
      isActive: classItem.isActive !== false,
      scheduleType: classItem.scheduleType || 'one-off',
      recurringPattern: classItem.recurringPattern || 'none',
      recurringDays: classItem.recurringDays || [],
      recurringStartDate: classItem.recurringStartDate ? new Date(classItem.recurringStartDate).toISOString().slice(0, 10) : '',
      recurringEndDate: classItem.recurringEndDate ? new Date(classItem.recurringEndDate).toISOString().slice(0, 10) : '',
      recurringInterval: classItem.recurringInterval || 1
    });
    setShowEditModal(true);
    setDropdownOpen(null);
  };

  const handleViewClassBookings = async (classItem) => {
    setSelectedClass(classItem);
    setLoadingBookings(true);
    setShowClassBookingsModal(true);
    setActiveTab('bookings'); // Reset to bookings tab
    
    try {
      // Fetch bookings, waitlist, and cancelled bookings data
      const [bookingsResponse, waitlistResponse, cancellationsResponse] = await Promise.all([
        axios.get(`/api/admin/classes/${classItem._id}/bookings`),
        axios.get(`/api/admin/classes/${classItem._id}/waitlist`),
        axios.get(`/api/admin/classes/${classItem._id}/cancellations`)
      ]);
      
      const allBookings = bookingsResponse.data.bookings || [];
      
      // For past classes, filter to show only unique users (including no-shows)
      if (new Date(classItem.startTime) < new Date()) {
        // Get all bookings that were confirmed/attended (including no-shows)
        const relevantBookings = allBookings.filter(b => b.status === 'attended' || b.status === 'confirmed' || b.status === 'no-show');
        const uniqueUsers = new Map();
        
        // Keep only the most recent booking for each user
        relevantBookings.forEach(booking => {
          const userId = booking.userId._id || booking.userId;
          if (!uniqueUsers.has(userId) || new Date(booking.bookingDate) > new Date(uniqueUsers.get(userId).bookingDate)) {
            uniqueUsers.set(userId, booking);
          }
        });
        
        setClassBookings(Array.from(uniqueUsers.values()));
      } else {
        // For future classes, show all bookings as usual
        setClassBookings(allBookings);
      }
      
      setWaitlistUsers(waitlistResponse.data.waitlist || []);
      setCancelledBookings(cancellationsResponse.data.cancellations || []);
    } catch (error) {
      console.error('Error fetching class data:', error);
      toast.error('Failed to fetch class data');
      setClassBookings([]);
      setWaitlistUsers([]);
      setCancelledBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleAddUserToClass = async () => {
    if (!selectedUserToAdd) {
      toast.error('Please select a user to add');
      return;
    }

    try {
      await axios.post('/api/admin/bookings/manual', {
        userId: selectedUserToAdd,
        classId: selectedClass._id
      });
      
      toast.success('User added to class successfully');
      
      // Refresh the bookings list
      const response = await axios.get(`/api/admin/classes/${selectedClass._id}/bookings`);
      setClassBookings(response.data.bookings || []);
      
      // Refresh the dashboard data to update booking counts
      fetchDashboardData();
      
      // Close the add user modal
      setShowAddUserModal(false);
      setSelectedUserToAdd('');
    } catch (error) {
      console.error('Error adding user to class:', error);
      toast.error('Failed to add user to class');
    }
  };

  const handleOpenAddUserModal = async () => {
    setLoadingUsers(true);
    setShowAddUserModal(true);
    
    try {
      // Get all users who are not already booked for this class
      const response = await axios.get('/api/admin/users');
      const allUsers = response.data.users || [];
      
      
      // Filter out users who are already booked for this class
      const bookedUserIds = classBookings.map(booking => booking.userId._id);

      // Only show users whose tags match this class. A class without a
      // concession type draws from the shared pool and is open to everyone;
      // a tagged class (e.g. "50+", "Yoga") only lists users enrolled in that
      // same tag.
      const classTag = selectedClass.concessionType
        ? String(selectedClass.concessionType).trim().toLowerCase()
        : null;

      const availableUsers = allUsers.filter(user => {
        if (bookedUserIds.includes(user._id) || !user.isActive) return false;
        if (!classTag) return true;
        const userTags = Array.isArray(user.concessionTypes)
          ? user.concessionTypes.map(t => String(t).trim().toLowerCase())
          : [];
        return userTags.includes(classTag);
      });
      
      setAvailableUsers(availableUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch available users');
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Removed unused handleViewWaitlist function

  const handleRemoveFromWaitlist = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the waitlist?')) {
      return;
    }

    try {
      // Check if this is a virtual instance ID and extract instanceStartTime
      let requestBody = {};
      if (selectedClass._id && selectedClass._id.includes('_') && selectedClass._id.includes('T')) {
        // This is a virtual instance ID, extract the instance start time
        const instanceTimeString = selectedClass._id.split('_')[1];
        requestBody.instanceStartTime = instanceTimeString;
      }
      
      await axios.delete(`/api/admin/classes/${selectedClass._id}/waitlist/${userId}`, {
        data: requestBody
      });
      toast.success('User removed from waitlist successfully');
      
      // Refresh the waitlist
      const response = await axios.get(`/api/admin/classes/${selectedClass._id}/waitlist`);
      setWaitlistUsers(response.data.waitlist || []);
    } catch (error) {
      console.error('Error removing user from waitlist:', error);
      toast.error('Failed to remove user from waitlist');
    }
  };

  const handleRemoveFromClass = async (booking) => {
    if (!window.confirm(`Are you sure you want to remove ${booking.userId.firstName} ${booking.userId.lastName} from this class?`)) {
      return;
    }

    try {
      await axios.delete(`/api/admin/bookings/manual/${booking._id}`);
      toast.success('User removed from class successfully');
      
      // Refresh the bookings list
      const response = await axios.get(`/api/admin/classes/${selectedClass._id}/bookings`);
      setClassBookings(response.data.bookings || []);
      
      // Refresh the dashboard data to update booking counts
      fetchDashboardData();
    } catch (error) {
      console.error('Error removing user from class:', error);
      toast.error('Failed to remove user from class');
    }
  };

  const handleToggleNoShow = async (booking) => {
    try {
      const newStatus = booking.status === 'no-show' ? 'attended' : 'no-show';
      await axios.put(`/api/admin/bookings/${booking._id}/status`, { status: newStatus });
      
      toast.success(`User marked as ${newStatus === 'no-show' ? 'no-show' : 'attended'}`);
      
      // Refresh the bookings list
      const response = await axios.get(`/api/admin/classes/${selectedClass._id}/bookings`);
      setClassBookings(response.data.bookings || []);
      
      // Refresh the dashboard data to update booking counts
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error('Failed to update attendance status');
    }
  };

  const handleCancelClass = (classItem) => {
    setClassToCancel(classItem);
    setShowCancelModal(true);
    setDropdownOpen(null);
  };

  const confirmCancelClass = async () => {
    if (!classToCancel) return;

    try {
      await axios.delete(`/api/admin/classes/${classToCancel._id}`);
      toast.success('Class cancelled successfully');
      
      // Refresh the dashboard data
      fetchDashboardData();
      
      // Close modal
      setShowCancelModal(false);
      setClassToCancel(null);
    } catch (error) {
      console.error('Error cancelling class:', error);
      toast.error('Failed to cancel class');
    }
  };

  const calculateDropdownPosition = (buttonElement) => {
    if (!buttonElement) return { top: '100%', bottom: 'auto', left: '0' };
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200; // Approximate height of dropdown
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
      // Position below
      return { 
        top: `${rect.bottom + 4}px`, 
        bottom: 'auto',
        left: `${rect.right - 180}px` // Align right edge of dropdown with right edge of button
      };
    } else {
      // Position above
      return { 
        top: 'auto', 
        bottom: `${viewportHeight - rect.top + 4}px`,
        left: `${rect.right - 180}px` // Align right edge of dropdown with right edge of button
      };
    }
  };

  const handleDropdownToggle = (classId, buttonElement) => {
    if (dropdownOpen === classId) {
      setDropdownOpen(null);
    } else {
      const position = calculateDropdownPosition(buttonElement);
      setDropdownPosition({ [classId]: position });
      setDropdownOpen(classId);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleSaveEdit = async () => {
    try {
      // Only send editable fields - scheduling fields are now immutable
      const cleanForm = {
        name: editForm.name,
        description: editForm.description,
        instructor: editForm.instructor,
        location: editForm.location,
        duration: editForm.duration,
        maxCapacity: editForm.maxCapacity,
        concessionsRequired: editForm.concessionsRequired,
        concessionType: editForm.concessionType || '',
        isActive: editForm.isActive
      };

      if (!cleanForm.concessionType) {
        toast.error('Please choose a concession type for this class');
        return;
      }
      
      // Note: All scheduling fields (startTime, scheduleType, recurringPattern, etc.) are now immutable
      // and cannot be changed once a class is created. Admins must create a new class to change scheduling.
      
      const response = await axios.put(`/api/admin/classes/${selectedClass._id}`, cleanForm);
      if (response.status === 200) {
        toast.success('Class updated successfully');
        setShowEditModal(false);
        fetchDashboardData(); // Refresh the dashboard data
      } else {
        toast.error('Failed to update class');
      }
    } catch (error) {
      console.error('Error updating class:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update class';
      toast.error(`Failed to update class: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
        <h1 className="text-2xl font-bold text-primary">
          {user?.role === 'coach' ? 'Coach Dashboard' : 'Admin Dashboard'}
        </h1>
        <p className="text-secondary mt-1">
          Overview of system statistics and recent activity
        </p>
      </div>

      {/* Quick Actions - Only for Admin users */}
      {user?.canEdit() && (
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <h2 className="text-lg font-semibold text-primary mb-4">Quick Actions</h2>
          <div className="grid gap-4 quick-actions-mobile">
            <button 
              onClick={() => navigate('/admin/users')}
              className="quick-action-button flex items-center justify-center p-4 border-2 border-primary rounded-lg hover:border-primary-hover hover:bg-hover transition-all duration-200 cursor-pointer"
            >
              <UserGroupIcon className="h-6 w-6 text-secondary mr-2" />
              <span className="text-sm font-medium text-primary">Manage Users</span>
            </button>
            
            <button 
              onClick={() => navigate('/admin/classes')}
              className="quick-action-button flex items-center justify-center p-4 border-2 border-primary rounded-lg hover:border-primary-hover hover:bg-hover transition-all duration-200 cursor-pointer"
            >
              <CalendarIcon className="h-6 w-6 text-secondary mr-2" />
              <span className="text-sm font-medium text-primary">Manage Classes</span>
            </button>
            


          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary">Total Users</p>
              <p className="text-2xl font-bold text-primary">{stats?.stats?.totalUsers || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BookOpenIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary">Total Active Bookings</p>
              <p className="text-2xl font-bold text-primary">{stats?.stats?.activeBookings || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Classes Table */}
      <div className="bg-card rounded-xl shadow-sm border border-primary w-full" style={{ overflow: 'visible' }}>
        <div className="p-6 border-b border-primary">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-primary">
              {classesActiveTab === 'upcoming' ? 'Upcoming Classes (Next 10)' : 'Class History (Last 30 Days)'}
            </h2>
            <div className="class-tabs-container">
              <button
                onClick={() => handleClassesTabChange('upcoming')}
                className={`class-tab-button ${
                  classesActiveTab === 'upcoming' ? 'active' : ''
                }`}
              >
                Upcoming
              </button>
              <button
                onClick={() => handleClassesTabChange('history')}
                className={`class-tab-button ${
                  classesActiveTab === 'history' ? 'active' : ''
                }`}
              >
                History
              </button>
            </div>
          </div>
        </div>
        <div 
          className="overflow-x-auto p-6"
          style={{
            overflowX: 'auto',
            overflowY: 'visible'
          }}
        >
          <table className="w-full border-collapse" style={{ minWidth: '800px' }}>
            <thead className="bg-hover">
              <tr>
                <th className="table-header table-header-left">
                  Class
                </th>
                <th className="table-header table-header-left">
                  Date & Time
                </th>
                {classesActiveTab === 'upcoming' ? (
                  <>
                    <th className="table-header table-header-left">
                      Bookings
                    </th>
                    <th className="table-header table-header-left">
                      Instructor
                    </th>
                    <th className="table-header table-header-left">
                      Type
                    </th>
                    <th className="table-header table-header-right">
                      Actions
                    </th>
                  </>
                ) : (
                  <>
                    <th className="table-header table-header-left">
                      Bookings
                    </th>
                    <th className="table-header table-header-left">
                      Instructor
                    </th>
                    <th className="table-header table-header-left">
                      Type
                    </th>
                    <th className="table-header table-header-right">
                      Attendance
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {currentClasses.length > 0 ? (
                currentClasses.map((cls) => {
                  const classDate = new Date(cls.startTime);
                  const isRecurring = cls.scheduleType === 'recurring';
                  // Removed unused isVirtualInstance variable
                  
                  return (
                    <tr key={cls._id} className="border-b border-primary hover:bg-hover">
                    <td className="table-cell table-cell-left">
                      <div className="flex items-center">
                        <div className={`table-class-icon ${classesActiveTab === 'history' ? 'table-class-icon-history' : ''}`}>
                          {classesActiveTab === 'upcoming' ? (
                            <CalendarIcon />
                          ) : (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                        <div className="table-class-info">
                          <div className="table-class-name">
                            {cls.name}
                          </div>
                        </div>
                      </div>
                    </td>
                      <td className="table-cell table-cell-left">
                        <div className="table-class-datetime">
                          {classDate.toLocaleDateString('en-NZ', { 
                            weekday: 'short', 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </div>
                        <div className="table-class-time">
                          {classDate.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false })}
                        </div>
                      </td>
                      {classesActiveTab === 'upcoming' ? (
                        <>
                          <td className="table-cell table-cell-left">
                            <div 
                              className="table-class-bookings"
                              onClick={() => handleViewClassBookings(cls)}
                            >
                              <div className="table-class-bookings-number">
                                {cls.activeBookings || 0}/{cls.maxCapacity || '∞'}
                              </div>
                              <div className="table-class-bookings-subtitle">
                                Click to manage
                              </div>
                            </div>
                          </td>
                          <td className="table-cell table-cell-left">
                            <div className="table-class-instructor">
                              {cls.instructor || 'TBD'}
                            </div>
                          </td>
                          <td className="table-cell table-cell-left">
                            <span className={`table-class-type ${isRecurring ? 'table-class-type-recurring' : 'table-class-type-oneoff'}`}>
                              {isRecurring ? 'Recurring' : 'One-off'}
                            </span>
                          </td>
                        </>
                    ) : (
                      <>
                        <td className="table-cell table-cell-left">
                          <div
                            className="table-class-bookings"
                            onClick={() => handleViewClassBookings(cls)}
                          >
                            <div className="table-class-bookings-number">
                              {cls.attendedBookings || 0} attended
                            </div>
                            <div className="table-class-bookings-subtitle">
                              Click to view details
                            </div>
                          </div>
                        </td>
                        <td className="table-cell table-cell-left">
                          <div className="table-class-instructor">
                            {cls.instructor || 'TBD'}
                          </div>
                        </td>
                        <td className="table-cell table-cell-left">
                          <span className={`table-class-type ${isRecurring ? 'table-class-type-recurring' : 'table-class-type-oneoff'}`}>
                            {isRecurring ? 'Recurring' : 'One-off'}
                          </span>
                        </td>
                        <td className="table-cell table-cell-right">
                          <div className="table-class-attendance">
                            <div className="table-class-attendance-rate">
                              {cls.attendanceRate || 0}%
                            </div>
                            <div className="table-class-attendance-detail">
                              {cls.attendedBookings || 0}/{cls.totalBookings || 0} attended
                            </div>
                          </div>
                        </td>
                      </>
                    )}
                      {classesActiveTab === 'upcoming' && (
                        <td className="table-cell table-cell-right">
                          {user?.canEdit() && (
                            <div className="dropdown-container">
                              <button
                                onClick={(e) => handleDropdownToggle(cls._id, e.currentTarget)}
                                className="dropdown-button"
                                >
                                <EllipsisVerticalIcon className="w-4 h-4" />
                              </button>

                              {dropdownOpen === cls._id && (
                                <div
                                  className="dropdown-menu-table"
                                  style={{
                                    ...dropdownPosition[cls._id]
                                  }}>
                                  <button
                                    onClick={() => handleEditClass(cls)}
                                    className="dropdown-item border-b border-primary"
                                    >
                                    <PencilIcon className="w-4 h-4 mr-2" />
                                    Edit Class
                                  </button>
                                  <button
                                    onClick={() => handleCancelClass(cls)}
                                    className="dropdown-item dropdown-item-danger"
                                    >
                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Cancel Class
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '48px 16px', textAlign: 'center' }}>
                    <div className="text-secondary">
                      <svg style={{ margin: '0 auto 8px', width: '48px', height: '48px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-muted">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v16a2 2 0 002 2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-primary">
                        {classesActiveTab === 'upcoming' ? 'No upcoming classes found' : 'No class history found'}
                      </h3>
                      <p className="mt-1 text-xs text-secondary">
                        {classesActiveTab === 'upcoming' 
                          ? 'No classes are scheduled in the next 30 days.'
                          : 'No classes have been held in the last 30 days.'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {currentClassesList.length > classesPerPage && (
          <div className="pagination-container rounded-xl">
            <div className="pagination-mobile">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="pagination-mobile-button"
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="pagination-mobile-button"
              >
                Next
              </button>
            </div>
            <div className="pagination-desktop">
              <div>
                <p className="pagination-info">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, currentClassesList.length)}</span> of{' '}
                  <span className="font-medium">{currentClassesList.length}</span> results
                  {currentClassesList.length > 10 * classesPerPage && (
                    <span className="text-xs text-secondary ml-1">(showing first 10 pages)</span>
                  )}
                </p>
              </div>
              <div>
                <nav className="pagination-nav" aria-label="Pagination">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-button arrow"
                  >
                    <span className="sr-only">Previous</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`pagination-button page-number ${page === currentPage ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-button arrow"
                  >
                    <span className="sr-only">Next</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>


                          
      {/* Add User to Class Modal */}
      {showAddUserModal && selectedClass && user?.canEdit() && (
        <div className="add-user-modal-overlay">
          <div className="add-user-modal-container">
            {/* Modal Header */}
            <div className="add-user-modal-header">
              <div>
                <h2 className="add-user-modal-title">
                  Add User to Class
                </h2>
                <p className="add-user-modal-subtitle">
                  {selectedClass.name} - {new Date(selectedClass.startTime).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => setShowAddUserModal(false)}
                className="add-user-modal-close-btn"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="add-user-modal-content">
              {loadingUsers ? (
                <div className="add-user-modal-loading">
                  Loading available users...
                </div>
              ) : availableUsers.length > 0 ? (
                <div>
                  <label className="form-label">
                    Select User to Add
                  </label>
                  <select
                    value={selectedUserToAdd}
                    onChange={(e) => setSelectedUserToAdd(e.target.value)}
                    className="add-user-modal-select"
                  >
                    <option value="">Choose a user...</option>
                    {availableUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName} (@{user.username})
                      </option>
                    ))}
                  </select>
                  
                  <div className="add-user-modal-info">
                    {availableUsers.length} available user{availableUsers.length !== 1 ? 's' : ''} found
                  </div>
                </div>
              ) : (
                <div className="add-user-modal-empty-state">
                  <UserGroupIcon className="add-user-modal-empty-icon" />
                  <p className="add-user-modal-empty-title">
                    No available users
                  </p>
                  <p className="add-user-modal-empty-description">
                    All active users are already booked for this class.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="add-user-modal-footer">
              <button
                onClick={() => setShowAddUserModal(false)}
                className="add-user-modal-btn-cancel"
              >
                Cancel
              </button>
              {availableUsers.length > 0 && (
                <button
                  onClick={handleAddUserToClass}
                  disabled={!selectedUserToAdd}
                  className="add-user-modal-btn-add"
                >
                  Add User
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Waitlist Modal - REMOVED - Now using tabs */}
      {false && showWaitlistModal && selectedClass && (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
              maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 24px 0 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexShrink: 0
            }}>
              <div>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0,
                  marginBottom: '4px'
                }}>
                  {selectedClass.name} - Waitlist
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  {new Date(selectedClass.startTime).toLocaleDateString()} at {new Date(selectedClass.startTime).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </p>
              </div>
              <button
                onClick={() => setShowWaitlistModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: '24px',
              flex: 1,
              overflow: 'auto'
            }}>
              {/* Waitlist Description */}
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#0c4a6e',
                  margin: 0,
                  marginBottom: '8px'
                }}>
                  How the Waitlist Works
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: '#075985',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  When a user cancels their booking, all users on the waitlist will be notified via email. 
                  It's first-come, first-served - users must click the link in the email and book the class themselves. 
                  No automatic promotions occur.
                </p>
              </div>

              {loadingWaitlist ? (
              <div style={{
                display: 'flex',
                  justifyContent: 'center',
                alignItems: 'center',
                  padding: '40px',
                  color: '#6b7280'
                }}>
                  Loading waitlist...
                </div>
              ) : waitlistUsers.length > 0 ? (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: 0
                    }}>
                      {waitlistUsers.length} user{waitlistUsers.length !== 1 ? 's' : ''} on waitlist
                    </p>
                  </div>

                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead>
                        <tr style={{
                          backgroundColor: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '12px',
                  fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            Position
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            User
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            Joined
                          </th>
                          <th style={{
                            padding: '12px 16px',
                            textAlign: 'right',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {waitlistUsers.map((waitlistEntry) => (
                          <tr key={waitlistEntry.userId} style={{
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <td style={{ padding: '16px' }}>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '500',
                  color: '#111827'
                }}>
                                #{waitlistEntry.position}
                              </div>
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#111827',
                                  marginBottom: '2px'
                                }}>
                                  {waitlistEntry.user.firstName} {waitlistEntry.user.lastName}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6b7280'
                                }}>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div style={{
                                fontSize: '14px',
                                color: '#374151'
                              }}>
                                {new Date(waitlistEntry.joinedAt).toLocaleDateString()}
                              </div>
                            </td>
                            <td style={{ padding: '16px', textAlign: 'right' }}>
                              {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) > new Date() && (
                                <button
                                  onClick={() => handleRemoveFromWaitlist(waitlistEntry.userId)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#ef4444',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
              </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280'
                }}>
                  No users on waitlist
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ 
              padding: '0 24px 24px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowWaitlistModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* Class Bookings Modal */}
      {showClassBookingsModal && selectedClass && (
        <div className="modal-overlay-mobile">
          <div className="bookings-modal-container">
            {/* Modal Header */}
            <div className="bookings-modal-header">
              <div>
                <h2 className="bookings-modal-title">
                  {selectedClass.name} - Bookings
                </h2>
                <p className="bookings-modal-subtitle">
                  {new Date(selectedClass.startTime).toLocaleDateString()} at {new Date(selectedClass.startTime).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </p>
              </div>
              <button
                onClick={() => setShowClassBookingsModal(false)}
                className="bookings-modal-close-btn"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-tertiary)',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--bg-hover)';
                  e.target.style.color = 'var(--text-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--bg-tertiary)';
                  e.target.style.color = 'var(--text-tertiary)';
                }}
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="bookings-modal-tabs">
              <button
                onClick={() => setActiveTab('bookings')}
                className={`bookings-modal-tab ${activeTab === 'bookings' ? 'active' : ''}`}
              >
                {selectedClass && new Date(selectedClass.startTime) > new Date() ? 'Confirmed Bookings' : 'Attended'} ({classBookings.length})
              </button>
              <button
                onClick={() => setActiveTab('waitlist')}
                className={`bookings-modal-tab ${activeTab === 'waitlist' ? 'active' : ''}`}
              >
                Waitlist ({waitlistUsers.length})
              </button>
              <button
                onClick={() => setActiveTab('cancellations')}
                className={`bookings-modal-tab ${activeTab === 'cancellations' ? 'active' : ''}`}
              >
                Cancellations ({cancelledBookings.length})
              </button>
            </div>

            {/* Modal Content */}
            <div className="bookings-modal-content">
              {activeTab === 'bookings' && (
                <>
                  {loadingBookings ? (
                    <div className="bookings-modal-loading">
                      Loading bookings...
                    </div>
                  ) : classBookings.length > 0 ? (
                <div>
                  <div className="bookings-modal-actions" style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    marginBottom: '16px' 
                  }}>
                    {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) > new Date() && (
                      <button
                        onClick={handleOpenAddUserModal}
                        className="bookings-modal-btn-primary"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          padding: '6px 12px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#3b82f6';
                        }}
                      >
                        <span style={{ fontSize: '12px', lineHeight: '1' }}>+</span>
                        Add User to Class
                      </button>
                    )}
                  </div>
                  
                  <div className="bookings-modal-table-container">
                    <table className="bookings-modal-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Booked Date</th>
                          <th>Attendance</th>
                          {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) < new Date() && (
                            <th>No Show</th>
                          )}
                          {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) > new Date() && (
                            <th className="text-right">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {classBookings.map((booking) => (
                          <tr key={booking._id}>
                            <td>
                              <div className="bookings-modal-user-info">
                                <div className="bookings-modal-user-name">
                                  {booking.userId.firstName} {booking.userId.lastName}
                                </div>
                                <div className="bookings-modal-user-details">
                                  @{booking.userId.username}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="bookings-modal-date">
                                {new Date(booking.bookingDate).toLocaleDateString('en-NZ', {
                                  timeZone: 'Pacific/Auckland',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </div>
                            </td>
                            <td>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                color: booking.status === 'no-show' ? '#ef4444' : '#10b981'
                              }}>
                                {selectedClass && new Date(selectedClass.startTime) < new Date() ? (
                                  booking.status === 'no-show' ? 'No Show' : 'Attended'
                                ) : (
                                  booking.status === 'confirmed' ? 'Confirmed' : booking.status
                                )}
                              </span>
                            </td>
                            {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) < new Date() && (
                              <td>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '6px' }}>
                                  <div style={{
                                    position: 'relative',
                                    width: '44px',
                                    height: '24px',
                                    backgroundColor: booking.status === 'no-show' ? '#ef4444' : '#d1d5db',
                                    borderRadius: '12px',
                                    transition: 'background-color 0.2s ease',
                                    cursor: 'pointer'
                                  }}>
                                    <div style={{
                                      position: 'absolute',
                                      top: '2px',
                                      left: booking.status === 'no-show' ? '22px' : '2px',
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: 'white',
                                      borderRadius: '50%',
                                      transition: 'left 0.2s ease',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }} />
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={booking.status === 'no-show'}
                                    onChange={() => handleToggleNoShow(booking)}
                                    style={{ display: 'none' }}
                                  />
                                </label>
                              </td>
                            )}
                            {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) > new Date() && (
                              <td className="text-right">
                                <button
                                  onClick={() => handleRemoveFromClass(booking)}
                                  className="bookings-modal-remove-btn"
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#dc2626';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#ef4444';
                                  }}
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bookings-modal-empty-state" style={{
                  textAlign: 'center',
                  padding: '48px 24px',
                  color: 'var(--text-tertiary)'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: '0 0 8px 0'
                  }}>
                    No bookings yet
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: 'var(--text-tertiary)',
                    margin: '0 0 24px 0'
                  }}>
                    {selectedClass && new Date(selectedClass.startTime) > new Date() 
                      ? "This class doesn't have any confirmed bookings yet."
                      : "This class doesn't have any attendees yet."
                    }
                  </p>
                  {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) > new Date() && (
                    <div className="bookings-modal-empty-actions">
                      <button
                        onClick={handleOpenAddUserModal}
                        className="bookings-modal-btn-primary"
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          padding: '6px 12px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          margin: '0 auto'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#3b82f6';
                        }}
                      >
                        <span style={{ fontSize: '12px', lineHeight: '1' }}>+</span>
                        Add User to Class
                      </button>
                    </div>
                  )}
                </div>
              )}
                </>
              )}

              {activeTab === 'waitlist' && (
                <>
                  {loadingBookings ? (
                    <div className="bookings-modal-loading">
                      Loading waitlist...
                    </div>
                  ) : waitlistUsers.length > 0 ? (
                    <div>
                      <div className="bookings-modal-waitlist-info">
                        <p className="bookings-modal-waitlist-text">
                          Users on the waitlist will be notified when a spot becomes available.
                          It's first-come, first-served - the first person to click the link and book gets the spot.
                        </p>
                      </div>
                      
                      <div className="bookings-modal-table-container">
                        <table className="bookings-modal-table">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Joined Waitlist</th>
                              <th className="text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {waitlistUsers.map((waitlistUser) => (
                              <tr key={waitlistUser._id}>
                                <td>
                                  <div className="bookings-modal-user-info">
                                    <div className="bookings-modal-user-name">
                                      {waitlistUser.user?.firstName} {waitlistUser.user?.lastName}
                                    </div>
                                    <div className="bookings-modal-user-details">
                                      @{waitlistUser.user?.username}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="bookings-modal-date">
                                    {new Date(waitlistUser.joinedAt).toLocaleDateString()}
                                  </div>
                                </td>
                                <td className="text-right">
                                  {user?.canEdit() && selectedClass && new Date(selectedClass.startTime) > new Date() && (
                                    <button
                                      onClick={() => handleRemoveFromWaitlist(waitlistUser._id)}
                                      className="bookings-modal-remove-btn"
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#dc2626';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#ef4444';
                                      }}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="bookings-modal-empty-state" style={{
                      textAlign: 'center',
                      padding: '48px 24px',
                      color: 'var(--text-tertiary)'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        margin: '0 0 8px 0'
                      }}>
                        No waitlist
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: 'var(--text-tertiary)',
                        margin: '0'
                      }}>
                        No users are currently on the waitlist for this class.
                      </p>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'cancellations' && (
                <>
                  {loadingCancellations ? (
                    <div className="bookings-modal-loading">
                      Loading cancellations...
                    </div>
                  ) : cancelledBookings.length > 0 ? (
                    <div>
                      <div className="bookings-modal-cancellations-info">
                        <p className="bookings-modal-cancellations-text">
                          Users who cancelled their bookings for this class. This includes both user-initiated and admin-initiated cancellations.
                        </p>
                      </div>
                      
                      <div className="bookings-modal-table-container">
                        <table className="bookings-modal-table">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Booked Date</th>
                              <th>Cancelled Date</th>
                              <th>Refund</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cancelledBookings.map((booking) => (
                              <tr key={booking._id}>
                                <td>
                                  <div className="bookings-modal-user-info">
                                    <div className="bookings-modal-user-name">
                                      {booking.userId.firstName} {booking.userId.lastName}
                                    </div>
                                    <div className="bookings-modal-user-details">
                                      @{booking.userId.username}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="bookings-modal-date">
                                    {new Date(booking.bookingDate).toLocaleDateString()}
                                  </div>
                                </td>
                                <td>
                                  <div className="bookings-modal-date">
                                    {booking.cancellationDate ? new Date(booking.cancellationDate).toLocaleDateString() : 'N/A'}
                                  </div>
                                </td>
                                <td>
                                  <div className="bookings-modal-refund-info" style={{
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    padding: '8px 0'
                                  }}>
                                    {booking.refundedConcessions > 0 ? (
                                      <span style={{ color: '#10b981', fontWeight: '500' }}>
                                        +{booking.refundedConcessions} concessions
                                      </span>
                                    ) : (
                                      <span style={{ color: '#6b7280' }}>
                                        No refund
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="bookings-modal-empty-state" style={{
                      textAlign: 'center',
                      padding: '48px 24px',
                      color: 'var(--text-tertiary)'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        margin: '0 0 8px 0'
                      }}>
                        No cancellations
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: 'var(--text-tertiary)',
                        margin: '0'
                      }}>
                        No users have cancelled their bookings for this class.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bookings-modal-footer" style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid var(--border-primary)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <button
                onClick={() => setShowClassBookingsModal(false)}
                className="bookings-modal-btn-secondary"
                style={{ 
                  padding: '10px 24px',
                  backgroundColor: 'var(--secondary)',
                  color: 'var(--text-inverse)',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--secondary-hover)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(100, 116, 139, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--secondary)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Class Modal */}
      {showEditModal && selectedClass && user?.canEdit() && (
        <div 
          className="modal-overlay-mobile"
          onClick={() => setShowEditModal(false)}
        >
          <div 
            className="modal-container-mobile"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="modal-header-mobile">
              <h3 className="modal-title text-primary text-center">Edit Class</h3>
            </div>

            {/* Modal Content */}
            <div className="modal-body-mobile">
              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Class Name
                </label>
                <input
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="form-input w-full px-3 py-2 rounded-md text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Description
                </label>
                <textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                  className="form-input w-full px-3 py-2 rounded-md text-sm resize-vertical"
                />
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Instructor
                </label>
                <input
                  type="text"
                  value={editForm.instructor || ''}
                  onChange={(e) => setEditForm({...editForm, instructor: e.target.value})}
                  className="form-input w-full px-3 py-2 rounded-md text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Location
                </label>
                <input
                  type="text"
                  value={editForm.location || ''}
                  onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                  className="form-input w-full px-3 py-2 rounded-md text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Start Time
                </label>
                <input
                  type="datetime-local"
                  value={editForm.startTime || ''}
                  disabled
                  className="form-input w-full px-3 py-2 rounded-md text-sm immutable-field"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ⚠️ Start time cannot be changed. Create a new class to change the schedule.
                </p>
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={editForm.duration || 60}
                  onChange={(e) => setEditForm({...editForm, duration: parseInt(e.target.value)})}
                  min="15"
                  max="480"
                  className="form-input w-full px-3 py-2 rounded-md text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Max Capacity
                </label>
                <input
                  type="number"
                  value={editForm.maxCapacity || 20}
                  onChange={(e) => setEditForm({...editForm, maxCapacity: parseInt(e.target.value)})}
                  min="1"
                  className="form-input w-full px-3 py-2 rounded-md text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Concessions Required
                </label>
                <input
                  type="number"
                  value={editForm.concessionsRequired || 1}
                  onChange={(e) => setEditForm({...editForm, concessionsRequired: parseInt(e.target.value)})}
                  min="0"
                  className="form-input w-full px-3 py-2 rounded-md text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="form-label-small text-secondary">
                  Concession Type *
                </label>
                <select
                  value={editForm.concessionType || ''}
                  onChange={(e) => setEditForm({...editForm, concessionType: e.target.value})}
                  className="form-input w-full px-3 py-2 rounded-md text-sm"
                >
                  <option value="">— required —</option>
                  {concessionTypes.filter(t => !t.isShared).map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.displayName}
                    </option>
                  ))}
                </select>
              </div>



            </div>

            {/* Modal Footer */}
            <div className="modal-footer-mobile">
              <div className="modal-footer-content">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary mr-4"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Class Modal */}
      {user?.canEdit() && (
        <ConfirmModal
          isOpen={showCancelModal}
        title="Cancel Class?"
        message={classToCancel ? `Are you sure you want to cancel the class "${classToCancel.name}"? This will notify all booked users and refund their concessions.` : ''}
        confirmText="Yes, cancel class"
        cancelText="Keep class"
          onConfirm={confirmCancelClass}
          onCancel={() => {
            setShowCancelModal(false);
            setClassToCancel(null);
          }}
        />
      )}

      {/* Add User to Class Modal - Positioned last for highest z-index */}
      {showAddUserModal && selectedClass && user?.canEdit() && (
        <div 
          className="modal-overlay-mobile"
          style={{ zIndex: 9999 }}
        >
          <div className="add-user-modal-container">
            {/* Modal Header */}
            <div className="add-user-modal-header">
              <div>
                <h2 className="add-user-modal-title">
                  Add User to Class
                </h2>
                <p className="add-user-modal-subtitle">
                  {selectedClass.name} - {new Date(selectedClass.startTime).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => setShowAddUserModal(false)}
                className="add-user-modal-close-btn"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-tertiary)',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--bg-hover)';
                  e.target.style.color = 'var(--text-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--bg-tertiary)';
                  e.target.style.color = 'var(--text-tertiary)';
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="add-user-modal-content">
              {loadingUsers ? (
                <div className="add-user-modal-loading">
                  Loading available users...
                </div>
              ) : availableUsers.length > 0 ? (
                <div>
                  <label className="form-label">
                    Select User to Add
                  </label>
                  <select
                    value={selectedUserToAdd}
                    onChange={(e) => setSelectedUserToAdd(e.target.value)}
                    className="add-user-modal-select"
                  >
                    <option value="">Choose a user...</option>
                    {availableUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName} (@{user.username})
                      </option>
                    ))}
                  </select>
                  
                  <div className="add-user-modal-info">
                    {availableUsers.length} available user{availableUsers.length !== 1 ? 's' : ''} found
                  </div>
                </div>
              ) : (
                <div className="add-user-modal-empty-state">
                  <UserGroupIcon className="add-user-modal-empty-icon" />
                  <p className="add-user-modal-empty-title">
                    No available users
                  </p>
                  <p className="add-user-modal-empty-description">
                    All active users are already booked for this class.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="add-user-modal-footer" style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid var(--border-primary)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <button
                onClick={() => setShowAddUserModal(false)}
                className="add-user-modal-btn-cancel"
                style={{ 
                  padding: '10px 24px',
                  backgroundColor: 'var(--secondary)',
                  color: 'var(--text-inverse)',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--secondary-hover)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(100, 116, 139, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--secondary)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Cancel
              </button>
              {availableUsers.length > 0 && (
                <button
                  onClick={handleAddUserToClass}
                  disabled={!selectedUserToAdd}
                  className="add-user-modal-btn-add"
                  style={{ 
                    padding: '10px 24px',
                    backgroundColor: selectedUserToAdd ? 'var(--primary)' : 'var(--text-muted)',
                    color: 'var(--text-inverse)',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '500',
                    cursor: selectedUserToAdd ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    opacity: selectedUserToAdd ? 1 : 0.6
                  }}
                  onMouseEnter={(e) => {
                    if (selectedUserToAdd) {
                      e.target.style.backgroundColor = 'var(--primary-hover)';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedUserToAdd) {
                      e.target.style.backgroundColor = 'var(--primary)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  Add User
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;

